// Shared by the client, server and edge Sentry configs.

type SentryEnvironment = "production" | "staging";

/**
 * Only production and staging report to Sentry. Each Vercel project sets
 * NEXT_PUBLIC_SENTRY_ENVIRONMENT for its Production deployments (revalytics: "production",
 * revalytics-staging: "staging"); previews and local runs leave it unset and send nothing.
 * A production deployment without the variable still reports, as "production", so a missed
 * setting can't switch Sentry off unnoticed.
 */
export function resolveSentryEnvironment(
  explicit: string | undefined,
  vercelEnv: string | undefined,
): SentryEnvironment | undefined {
  if (explicit === "production" || explicit === "staging") return explicit;
  if (!explicit && vercelEnv === "production") return "production";
  return undefined;
}

export const SENTRY_ENVIRONMENT = resolveSentryEnvironment(
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV,
);
export const SENTRY_ENABLED = SENTRY_ENVIRONMENT !== undefined;
// Performance data and replays come from production only; staging reports errors and nothing else.
export const SENTRY_IS_PRODUCTION = SENTRY_ENVIRONMENT === "production";

type EventText = { message?: string; exception?: { values?: { value?: string }[] } };

// Issues to thin out because they flood the quota. Empty on purpose: a listed issue shows only
// about 1% of its real count in Sentry, and nothing removes an entry once its fix ships - someone
// has to. Add one only to stop a flood, with a comment saying where it comes from, and remove it
// in the PR that fixes it.
const KNOWN_NOISE: RegExp[] = [];

// One known-noise event in 100 still goes through, so the issue stays visible in Sentry.
const KNOWN_NOISE_KEEP_RATE = 0.01;

export function isKnownNoise(event: EventText, patterns: RegExp[] = KNOWN_NOISE): boolean {
  const text = event.exception?.values?.[0]?.value ?? event.message ?? "";
  return patterns.some(pattern => pattern.test(text));
}

export function dropKnownNoise(
  event: EventText,
  random = Math.random(),
  patterns: RegExp[] = KNOWN_NOISE,
): boolean {
  return isKnownNoise(event, patterns) && random >= KNOWN_NOISE_KEEP_RATE;
}
